import React, { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { iceSmoothScroll } from '../utils/smoothScroll'
import { sendMetaEvent } from '../utils/metaCapi'
import LanguageSwitcher from './LanguageSwitcher'

const Navbar = () => {
  const [homeScrolled, setHomeScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [activeSection, setActiveSection] = useState('')

  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const isHome = location.pathname === '/'
  const isScrolled = !isHome || homeScrolled

  useEffect(() => {
    if (location.pathname !== '/') return

    const handleScroll = () => {
      setHomeScrolled(window.scrollY > 50)
      
      const sections = ['about', 'services', 'process', 'pricing', 'faq', 'cta']
      const scrollPosition = window.scrollY + 200
      
      for (const section of sections) {
        const element = document.getElementById(section)
        if (element) {
          const { offsetTop, offsetHeight } = element
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section)
            break
          }
        }
      }
    }
    
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [location.pathname])

  const handleNavClick = (e, link) => {
    e.preventDefault()
    setIsMobileMenuOpen(false)

    sendMetaEvent('SectionClick', {
      content_name: link.name,
      section_id: link.id,
      page_path: '/',
    })
    
    if (link.href.startsWith('/#')) {
      if (location.pathname === '/') {
        const targetId = link.href.replace('/#', '#')
        iceSmoothScroll(targetId, 1200)
      } else {
        navigate('/')
        setTimeout(() => {
          const targetId = link.href.replace('/#', '#')
          iceSmoothScroll(targetId, 1200)
        }, 100)
      }
    } else {
      navigate(link.href)
    }
  }

  const isLinkActive = (link) => {
    if (link.href.startsWith('/#')) return activeSection === link.id
    return location.pathname === link.href
  }

  const navLinks = [
    { name: t('nav.about', 'About'), href: '/#about', id: 'about' },
    { name: t('nav.services', 'Services'), href: '/#services', id: 'services' },
    { name: t('nav.process', 'Process'), href: '/#process', id: 'process' },
    { name: t('nav.pricing', 'Pricing'), href: '/#pricing', id: 'pricing' },
    { name: t('nav.packages', 'Packages'), href: '/packages', id: 'packages' },
    { name: t('nav.faq', 'FAQ'), href: '/#faq', id: 'faq' },
    { name: t('nav.blog', 'Blog'), href: '/blog', id: 'blog' },
  ]

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-[#0a0a0f]/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
    }`}>
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" onClick={(e) => { 
            if (location.pathname === '/') {
              e.preventDefault(); 
              iceSmoothScroll('body', 1500) 
            }
          }}>
            <div className="w-12 h-12 bg-transparent flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <img src="/nodatx.png" alt={t('nav.logoAlt', 'Nodatx Logo')} className="w-full h-full object-contain" />
            </div>
            <span className="text-xl font-bold text-white">
              Noda<span className="text-purple-400">tx</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => handleNavClick(e, link)}
                className={`relative px-4 py-2 rounded-lg font-medium cursor-pointer transition-all duration-300
                  ${isLinkActive(link) 
                    ? 'text-purple-400 bg-purple-400/10' 
                    : 'text-gray-300 hover:text-purple-400 hover:bg-white/5'
                  }
                  hover:scale-105 active:scale-95
                `}
              >
                {link.name}
                {isLinkActive(link) && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-purple-400 rounded-full"></span>
                )}
              </a>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-2">
            <a 
              href="https://cal.com/mostafa-elsheikh/30min" 
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-purple-600 text-white font-semibold rounded-xl transition-all duration-200 hover:scale-105 active:scale-[0.98] hover:shadow-[0_0_30px_-5px_rgba(139,92,246,0.5)]"
              onClick={() => sendMetaEvent('Lead', 'book_call')}
            >
              {t('nav.bookCall', 'Book a Call')}
            </a>
            <LanguageSwitcher />
          </div>

          {/* Mobile Menu Button + LanguageSwitcher */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <motion.button
              className="text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              whileTap={{ scale: 0.9 }}
            >
            <motion.div
              key={isMobileMenuOpen ? 'close' : 'open'}
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </motion.div>
          </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              className="md:hidden bg-[#0a0a0f] border-t border-white/10 rounded-b-2xl overflow-hidden"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <motion.div
                className="py-4 px-4 space-y-2"
                initial="hidden"
                animate="visible"
                variants={{
                  visible: { transition: { staggerChildren: 0.05 } },
                }}
              >
                {navLinks.map((link, i) => (
                  <motion.a
                    key={link.name}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link)}
                    variants={{
                      hidden: { opacity: 0, x: -20 },
                      visible: { opacity: 1, x: 0 },
                    }}
                    transition={{ duration: 0.25 }}
                    className={`block px-4 py-3 rounded-lg transition-all duration-300 cursor-pointer
                      ${isLinkActive(link) 
                        ? 'text-purple-400 bg-purple-400/10 font-medium' 
                        : 'text-gray-300 hover:text-purple-400 hover:bg-white/5'
                      }
                    `}
                  >
                    {link.name}
                  </motion.a>
                ))}
                <motion.a
                  href="https://cal.com/mostafa-elsheikh/30min"
                  target="_blank"
                  rel="noreferrer"
                  variants={{
                    hidden: { opacity: 0, y: 10 },
                    visible: { opacity: 1, y: 0 },
                  }}
                  transition={{ duration: 0.25 }}
                  className="block text-center px-6 py-3 bg-purple-600 text-white font-semibold rounded-xl mt-4 transition-all duration-200 active:scale-[0.98] hover:shadow-[0_0_30px_-5px_rgba(139,92,246,0.5)]"
                  onClick={() => sendMetaEvent('Lead', 'book_call')}
                >
                  {t('nav.bookCall', 'Book a Call')}
                </motion.a>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  )
}

export default Navbar